import React, { useState, useMemo, useCallback } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { FormControl } from 'react-bootstrap'
import { Button as ShadcnButton } from '@/features/shadcn/ui/button'
import { Checkbox } from '@/features/shadcn/ui/checkbox'
import { LoaderCircle, X, ChevronDown, ChevronUp } from 'lucide-react'
import _ from 'lodash'
import { FREE_TEXT_FILTER_FIELDS, RANGE_FILTER_FIELDS } from '../filterFields'

// Re-export so existing `import { FREE_TEXT_FILTER_FIELDS } from
// './TrafficFilterSideMenu'` callers continue to work.
export { FREE_TEXT_FILTER_FIELDS, RANGE_FILTER_FIELDS }

export type PropertyOption = { label: string; value: string }
export type RangeValue = { min?: string | number; max?: string | number }
export type FilterRow = { field: string; value: string | string[] | RangeValue; op?: string }

type Props = {
  form: UseFormReturn<any>
  filters: FilterRow[]
  propertyOptions: PropertyOption[]
  optionsCache: Record<string, { label: string; value: string }[]>
  loadingFields: Record<string, boolean>
  onEnsureOptions: (fieldName: string) => void
  onApply: () => void
  applyDisabled?: boolean
}

function normalizeValues(v: string | string[] | RangeValue | undefined): string[] {
  if (v == null || v === '') return []
  if (Array.isArray(v)) return v.filter(Boolean)
  if (typeof v === 'object') return [] // range filters are handled separately
  return [String(v)]
}

export default function TrafficFilterSideMenu({
  form,
  filters,
  propertyOptions,
  optionsCache,
  loadingFields,
  onEnsureOptions,
  onApply,
  applyDisabled,
}: Props) {
  const [search, setSearch] = useState('')
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})

  const filterableProps = useMemo(
    () => propertyOptions.filter((p) => p.value),
    [propertyOptions]
  )

  const filteredProps = useMemo(() => {
    if (!search.trim()) return filterableProps
    const q = search.toLowerCase()
    return filterableProps.filter((p) => p.label.toLowerCase().includes(q))
  }, [filterableProps, search])

  const activeTags = useMemo(() => {
    return (filters || [])
      .filter((f) => {
        if (!f.field) return false
        if (RANGE_FILTER_FIELDS.has(f.field)) {
          const r: any = f.value
          return r && typeof r === 'object' && (r.min !== '' && r.min != null || r.max !== '' && r.max != null)
        }
        return normalizeValues(f.value as any).length > 0
      })
      .map((f) => {
        const label = propertyOptions.find((p) => p.value === f.field)?.label || f.field
        let display: string
        if (RANGE_FILTER_FIELDS.has(f.field)) {
          const r: any = f.value
          const min = r?.min !== '' && r?.min != null ? String(r.min) : ''
          const max = r?.max !== '' && r?.max != null ? String(r.max) : ''
          if (min && max) display = `${min} – ${max}`
          else if (min) display = `≥ ${min}`
          else display = `≤ ${max}`
        } else {
          const vals = normalizeValues(f.value as any)
          display = vals.length > 2 ? `${vals.slice(0, 2).join(', ')} +${vals.length - 2}` : vals.join(', ')
        }
        return { field: f.field, label, display }
      })
  }, [filters, propertyOptions])

  const setFilterForField = useCallback(
    (field: string, selectedValues: string[]) => {
      const current = [...(form.getValues('filters') || [])]
      const idx = current.findIndex((f) => f.field === field)

      if (selectedValues.length === 0) {
        if (idx >= 0) {
          if (current.length === 1) {
            form.setValue('filters', [{ field: '', value: '' }])
          } else {
            const next = current.filter((_, i) => i !== idx)
            form.setValue('filters', next.length ? next : [{ field: '', value: '' }])
          }
        }
        return
      }

      const row = { field, value: selectedValues.length === 1 ? selectedValues[0] : selectedValues }
      if (idx >= 0) {
        const next = [...current]
        next[idx] = row
        form.setValue('filters', next)
      } else {
        const emptyIdx = current.findIndex((f) => !f.field || f.field === '')
        if (emptyIdx >= 0) {
          const next = [...current]
          next[emptyIdx] = row
          form.setValue('filters', next)
        } else {
          form.setValue('filters', [...current, row])
        }
      }
    },
    [form]
  )

  const setRangeForField = useCallback(
    (field: string, range: RangeValue) => {
      const current = [...(form.getValues('filters') || [])]
      const idx = current.findIndex((f) => f.field === field)
      const cleared = (range.min === '' || range.min == null) && (range.max === '' || range.max == null)

      if (cleared) {
        if (idx >= 0) {
          if (current.length === 1) {
            form.setValue('filters', [{ field: '', value: '' }])
          } else {
            const next = current.filter((_, i) => i !== idx)
            form.setValue('filters', next.length ? next : [{ field: '', value: '' }])
          }
        }
        return
      }

      const row: any = { field, op: 'range', value: range }
      if (idx >= 0) {
        const next = [...current]
        next[idx] = row
        form.setValue('filters', next)
      } else {
        const emptyIdx = current.findIndex((f) => !f.field || f.field === '')
        if (emptyIdx >= 0) {
          const next = [...current]
          next[emptyIdx] = row
          form.setValue('filters', next)
        } else {
          form.setValue('filters', [...current, row])
        }
      }
    },
    [form]
  )

  const toggleValue = (field: string, optionValue: string, checked: boolean) => {
    const row = filters.find((f) => f.field === field)
    const cur = normalizeValues(row?.value)
    const next = checked ? _.uniq([...cur, optionValue]) : cur.filter((v) => v !== optionValue)
    setFilterForField(field, next)
  }

  const clearAll = () => {
    form.setValue('filters', [{ field: '', value: '' }])
  }

  const toggleExpand = (field: string) => {
    const next = !expanded[field]
    setExpanded((e) => ({ ...e, [field]: next }))
    if (next && !FREE_TEXT_FILTER_FIELDS.has(field) && !RANGE_FILTER_FIELDS.has(field)) {
      onEnsureOptions(field)
    }
  }

  const [freeTextDraft, setFreeTextDraft] = useState<Record<string, string>>({})
  const addFreeTextValue = (field: string) => {
    // Accept "a, b, c" as three separate chips so users can paste a list.
    const parts = (freeTextDraft[field] || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
    if (parts.length === 0) return
    const row = filters.find((f) => f.field === field)
    const cur = normalizeValues(row?.value)
    const next = [...cur]
    for (const v of parts) if (!next.includes(v)) next.push(v)
    if (next.length === cur.length) return // all duplicates
    setFilterForField(field, next)
    setFreeTextDraft((d) => ({ ...d, [field]: '' }))
  }
  const removeFreeTextValue = (field: string, val: string) => {
    const row = filters.find((f) => f.field === field)
    const cur = normalizeValues(row?.value)
    setFilterForField(field, cur.filter((v) => v !== val))
  }

  return (
    <div className="tw-flex tw-flex-col tw-h-full tw-min-h-0 tw-w-full filter-row">
      <div className="tw-flex-1 tw-min-h-0 tw-overflow-y-auto tw-overflow-x-hidden tw-pb-2">
        <div className="tw-mb-3">
          <FormControl
            className="full-width filter-search-box"
            placeholder="Search filters..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {activeTags.length > 0 && (
          <div className="tw-mb-4 tw-p-3 tw-border tw-border-slate-200 tw-rounded-lg tw-bg-gradient-to-b tw-from-blue-50 tw-to-slate-50">
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
              <span className="tw-text-sm tw-font-medium tw-text-slate-700">
                Active filters ({activeTags.length})
              </span>
              <button
                type="button"
                className="tw-text-xs tw-text-blue-600 hover:tw-underline"
                onClick={clearAll}
              >
                Clear all
              </button>
            </div>
            <div className="tw-flex tw-flex-col tw-items-start tw-gap-y-2">
              {activeTags.map((tag) => (
                <div
                  key={tag.field}
                  className="tw-relative tw-pl-3 tw-pr-8 tw-py-1.5 tw-bg-white tw-border tw-border-blue-200 tw-rounded-full tw-text-xs tw-shadow-sm tw-max-w-full"
                >
                  <span className="tw-text-blue-600 tw-font-medium">{tag.label}: </span>
                  <span className="tw-text-slate-700 tw-break-all">{tag.display}</span>
                  <button
                    type="button"
                    className="tw-absolute tw-right-1 tw-top-1/2 tw--translate-y-1/2 tw-p-0.5 tw-rounded hover:tw-bg-slate-100"
                    aria-label={`Remove ${tag.label}`}
                    onClick={() => {
                      if (RANGE_FILTER_FIELDS.has(tag.field)) {
                        setRangeForField(tag.field, { min: '', max: '' })
                      } else {
                        setFilterForField(tag.field, [])
                      }
                    }}
                  >
                    <X className="tw-w-3.5 tw-h-3.5 tw-text-slate-500" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="filter-container tw-border tw-border-slate-200 tw-rounded-lg tw-bg-white tw-p-2">
          {filteredProps.map((prop) => {
            const isOpen = expanded[prop.value] ?? false
            const row = filters.find((f) => f.field === prop.value)
            const selected = normalizeValues(row?.value)
            const opts = optionsCache[prop.value] || []
            const loading = loadingFields[prop.value]

            return (
              <div key={prop.value} className="tw-border-b tw-border-slate-100 last:tw-border-0">
                <button
                  type="button"
                  className="tw-w-full tw-flex tw-items-center tw-justify-between tw-py-2.5 tw-px-1 tw-text-left hover:tw-bg-slate-50 tw-rounded"
                  onClick={() => toggleExpand(prop.value)}
                >
                  <h5 className="tw-text-sm tw-font-medium tw-mb-0 tw-text-slate-800">{prop.label}</h5>
                  <div className="tw-flex tw-items-center tw-gap-2">
                    {selected.length > 0 && (
                      <span className="tw-text-xs tw-bg-blue-100 tw-text-blue-700 tw-px-2 tw-py-0.5 tw-rounded-full">
                        {selected.length}
                      </span>
                    )}
                    {isOpen ? (
                      <ChevronUp className="tw-w-4 tw-h-4 tw-text-slate-400" />
                    ) : (
                      <ChevronDown className="tw-w-4 tw-h-4 tw-text-slate-400" />
                    )}
                  </div>
                </button>
                {isOpen && FREE_TEXT_FILTER_FIELDS.has(prop.value) && (
                  <div className="tw-pl-1 tw-pr-1 tw-pb-3">
                    <div className="tw-flex tw-gap-2 tw-mb-2">
                      <FormControl
                        type="text"
                        placeholder={`Add ${prop.label.toLowerCase()}...`}
                        value={freeTextDraft[prop.value] || ''}
                        onChange={(e: any) =>
                          setFreeTextDraft((d) => ({ ...d, [prop.value]: e.target.value }))
                        }
                        onKeyDown={(e: any) => {
                          if (e.key === 'Enter') {
                            e.preventDefault()
                            addFreeTextValue(prop.value)
                          }
                        }}
                      />
                      <ShadcnButton
                        type="button"
                        size="sm"
                        onClick={() => addFreeTextValue(prop.value)}
                        disabled={!(freeTextDraft[prop.value] || '').trim()}
                      >
                        Add
                      </ShadcnButton>
                    </div>
                    {selected.length > 0 && (
                      <div className="tw-flex tw-flex-wrap tw-gap-1">
                        {selected.map((v) => (
                          <span
                            key={v}
                            className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-full tw-text-xs tw-text-slate-700"
                          >
                            {v}
                            <button
                              type="button"
                              className="tw-p-0 tw-text-slate-400 hover:tw-text-slate-700"
                              onClick={() => removeFreeTextValue(prop.value, v)}
                              aria-label={`Remove ${v}`}
                            >
                              <X className="tw-w-3 tw-h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {isOpen && RANGE_FILTER_FIELDS.has(prop.value) && (
                  <div className="tw-pl-1 tw-pr-1 tw-pb-3">
                    <div className="tw-flex tw-items-center tw-gap-2">
                      <FormControl
                        type="number"
                        placeholder="Min"
                        value={(row?.value as RangeValue)?.min ?? ''}
                        onChange={(e: any) =>
                          setRangeForField(prop.value, {
                            min: e.target.value,
                            max: (row?.value as RangeValue)?.max ?? '',
                          })
                        }
                      />
                      <span className="tw-text-slate-400 tw-text-xs">to</span>
                      <FormControl
                        type="number"
                        placeholder="Max"
                        value={(row?.value as RangeValue)?.max ?? ''}
                        onChange={(e: any) =>
                          setRangeForField(prop.value, {
                            min: (row?.value as RangeValue)?.min ?? '',
                            max: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>
                )}
                {isOpen && !FREE_TEXT_FILTER_FIELDS.has(prop.value) && !RANGE_FILTER_FIELDS.has(prop.value) && (
                  <div className="tw-pl-1 tw-pr-1 tw-pb-3 tw-max-h-56 tw-overflow-y-auto">
                    {loading && (
                      <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-slate-400 tw-py-2">
                        <LoaderCircle className="tw-w-4 tw-h-4 tw-animate-spin" />
                        Loading…
                      </div>
                    )}
                    {!loading &&
                      opts.map((opt) => (
                        <label
                          key={String(opt.value)}
                          className="tw-flex tw-items-center tw-gap-2 tw-py-1.5 tw-px-1 tw-rounded hover:tw-bg-slate-50 tw-cursor-pointer tw-text-sm"
                        >
                          <Checkbox
                            checked={selected.includes(String(opt.value))}
                            onCheckedChange={(c) =>
                              toggleValue(prop.value, String(opt.value), c === true)
                            }
                          />
                          <span className="tw-truncate">{opt.label}</span>
                        </label>
                      ))}
                    {!loading && opts.length === 0 && (
                      <div className="tw-text-sm tw-text-slate-400 tw-py-2">No values found</div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      <div className="filter-side-menu-footer tw-flex-shrink-0 tw-border-t tw-border-slate-200 tw-bg-white tw-pt-3 tw-pb-1 tw-mt-auto">
        <ShadcnButton type="button" className="tw-w-full tw-mb-2" disabled={applyDisabled} onClick={onApply}>
          Apply filters
        </ShadcnButton>
      </div>
    </div>
  )
}
